import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputField from "@/components/form/InputField";
import {
  Box,
  Card,
  Flex,
  Text,
  Heading,
  Container,
  Button,
} from "@radix-ui/themes";
import { authService } from "@/services/breeze/AuthService";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");

  const handleSubmit = async (values: { email: string }) => {
    setIsSubmitting(true);
    setError("");

    try {
      await authService.sendPasswordReset(values.email);

      setEmailAddress(values.email);
      setIsEmailSent(true);
    } catch (error) {
      console.error("Error sending password reset:", error);
      setError(
        "Failed to send password reset email. Please check your email address and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEmailSent) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="6">
              <Flex direction="column" align="center" gap="2">
                <Heading size="7" weight="bold" align="center">
                  Check Your Email
                </Heading>
                <Text color="gray" align="center" size="3">
                  We&apos;ve sent a password reset link to
                </Text>
                <Text weight="medium" align="center" size="3">
                  {emailAddress}
                </Text>
              </Flex>

              <Box
                p="4"
                style={{
                  backgroundColor: "var(--blue-a2)",
                  border: "1px solid var(--blue-a6)",
                  borderRadius: "var(--radius-3)",
                  width: "100%",
                }}
              >
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium" color="blue" align="center">
                    What to do next:
                  </Text>
                  <Text size="2" color="gray" align="center">
                    1. Check your email inbox (and spam folder)
                  </Text>
                  <Text size="2" color="gray" align="center">
                    2. Click the reset link in the email
                  </Text>
                  <Text size="2" color="gray" align="center">
                    3. Create your new password
                  </Text>
                </Flex>
              </Box>

              <Flex direction="column" gap="3" style={{ width: "100%" }}>
                <Button
                  onClick={() => {
                    setIsEmailSent(false);
                    setEmailAddress("");
                    setError("");
                  }}
                  variant="outline"
                  style={{ width: "100%" }}
                >
                  Send Another Email
                </Button>
                {searchParams.get("redirect_uri") && (
                  <Button
                    onClick={() => {
                      const redirectUri = searchParams.get("redirect_uri");
                      if (redirectUri) window.location.href = redirectUri;
                    }}
                    variant="outline"
                    style={{ width: "100%" }}
                  >
                    Continue to Application
                  </Button>
                )}
              </Flex>
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      size="1"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <Card style={{ width: "100%", maxWidth: "28rem" }}>
        <Box p="6">
          <Flex direction="column" gap="6">
            <Flex direction="column" align="center" gap="2">
              <Heading size="7" weight="bold" align="center">
                Forgot Your Password?
              </Heading>
              <Text color="gray" align="center" size="3">
                Enter your email address and we&apos;ll send you a link to reset
                your password.
              </Text>
            </Flex>

            {error && (
              <Box
                p="3"
                style={{
                  backgroundColor: "var(--red-a3)",
                  border: "1px solid var(--red-a6)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text color="red" size="2">
                  {error}
                </Text>
              </Box>
            )}

            <Formik
              initialValues={{
                email: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {() => (
                <Form>
                  <Flex direction="column" gap="4">
                    <InputField
                      name="email"
                      label="Email Address"
                      type="email"
                      required
                      options={{ placeholder: "Enter your email address" }}
                    />

                    <Button
                      type="submit"
                      loading={isSubmitting}
                      style={{ width: "100%" }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Sending Reset Link..."
                        : "Send Reset Link"}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>

            <Box style={{ textAlign: "center" }}>
              <Text size="2" color="gray" align="center">
                Remember your password?{" "}
                {searchParams.get("redirect_uri") ? (
                  <Text
                    style={{ cursor: "pointer" }}
                    color="blue"
                    weight="medium"
                    onClick={() => {
                      const redirectUri = searchParams.get("redirect_uri");
                      if (redirectUri) {
                        window.location.href = redirectUri;
                      }
                    }}
                  >
                    Continue to Application
                  </Text>
                ) : (
                  <Text color="gray" weight="medium">
                    Return to your application to log in
                  </Text>
                )}
              </Text>
            </Box>
          </Flex>
        </Box>
      </Card>
    </Container>
  );
}
